import { useQuery } from "@tanstack/react-query";
import { OnChangeFn, SortingState } from "@tanstack/react-table";
import { useCallback, useEffect, useMemo, useState } from "react";
import { getWorkspaceObjectSearchAgainstQuery } from "../../services/workspace/workspaceEndpoints";
import {
  buildObjectSearchQuery,
  normalizeObjectEntityType,
} from "./objectSearch";

type ObjectSearchSort = {
  field?: string;
  order?: "asc" | "desc";
};

type UseObjectSearchTableOptions<TData = any> = {
  workspaceId?: string;
  entityType?: string;
  enabled?: boolean;
  initialPageIndex?: number;
  initialPageSize?: number;
  initialSort?: ObjectSearchSort;
  queryOptions: Omit<Parameters<typeof buildObjectSearchQuery>[0], "pageIndex" | "pageSize" | "entityType" | "sort">;
  parseRows?: (rows: any[]) => TData[];
  keepPreviousData?: boolean;
  resetPageDeps?: any[];
};

const toSortingState = (sort?: ObjectSearchSort): SortingState => (
  sort?.field
    ? [{
      id: sort.field,
      desc: String(sort.order || "desc").toLowerCase() !== "asc",
    }]
    : []
);

const useObjectSearchTable = <TData = any>({
  workspaceId,
  entityType,
  enabled = true,
  initialPageIndex = 0,
  initialPageSize = 15,
  initialSort,
  queryOptions,
  parseRows,
  keepPreviousData = true,
  resetPageDeps = [],
}: UseObjectSearchTableOptions<TData>) => {
  const [pageIndex, setPageIndex] = useState(initialPageIndex);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [sorting, setSorting] = useState<SortingState>(toSortingState(initialSort));

  const activeSort = sorting?.[0];
  const sortField = activeSort?.id || initialSort?.field || queryOptions?.fallbackSortField || "";
  const sortOrder = activeSort?.desc ? "desc" : "asc";

  const normalizedEntityType = normalizeObjectEntityType(entityType);

  const query = useMemo(() => buildObjectSearchQuery({
    ...queryOptions,
    entityType: normalizedEntityType,
    pageIndex,
    pageSize,
    sort: {
      field: sortField,
      order: sortOrder,
    },
  }), [queryOptions, normalizedEntityType, pageIndex, pageSize, sortField, sortOrder]);

  const queryResult = useQuery({
    queryKey: ["object-search-table", workspaceId, normalizedEntityType, query],
    enabled: enabled && !!workspaceId && !!normalizedEntityType,
    keepPreviousData,
    queryFn: async () => getWorkspaceObjectSearchAgainstQuery({
      workspaceId: workspaceId as string,
      query: query as any,
    }),
  });

  const rows = useMemo(() => {
    const rawRows = queryResult?.data?.data || [];
    return parseRows ? parseRows(rawRows) : rawRows;
  }, [queryResult?.data?.data, parseRows]);

  const total = queryResult?.data?.total || 0;
  const pageCount = Math.max(1, Math.ceil(total / Math.max(1, pageSize)));

  const onSortingChange: OnChangeFn<SortingState> = useCallback((updater) => {
    setSorting((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      return (next || []).slice(0, 1);
    });
    setPageIndex(0);
  }, []);

  const setSort = useCallback((nextSort: ObjectSearchSort) => {
    setSorting(toSortingState(nextSort));
    setPageIndex(0);
  }, []);

  const resetPageKey = useMemo(() => JSON.stringify(resetPageDeps || []), [resetPageDeps]);

  useEffect(() => {
    if (resetPageDeps?.length) {
      setPageIndex(0);
    }
  }, [resetPageKey, resetPageDeps?.length]);

  return {
    rows,
    total,
    pageCount,
    pageIndex,
    pageSize,
    sorting,
    setSorting: onSortingChange,
    setSort,
    setPageIndex,
    setPageSize,
    isLoading: queryResult.isLoading,
    isFetching: queryResult.isFetching,
    error: queryResult.error,
    refetch: queryResult.refetch,
    query,
  };
};

export default useObjectSearchTable;
